import React, {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import {
  Edit,
  Check,
  Close,
  InfoOutlined,
  WarningAmberOutlined,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material'
import { AxiosResponse } from 'axios'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import { YesNoBadge } from '../../../components/YesNoBadge'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal } from '../../../hooks/useModal'
import { Courier } from '../../../interfaces/Courier'
import { PriorizationDDL } from '../priorizationddl'
import { useTypesPriority } from '../../../hooks/useTypesPriority'
import {
  sectionPaperSx,
  sectionTitleSx,
  createServiceColumns,
  DraftService,
  NAME_MAX_LENGTH
} from './config'
import {
  getCourier,
  putCourier,
  postCourierService,
  putCourierService,
  deleteCourierService
} from '../config/api'
import {
  GenericTableV2,
  createColumns,
  createAction
} from '../../../components/_EOS/GenericTable'

export interface CourierFormEditNewRef {
  save: () => void
}

interface CourierFormEditNewProps {
  courierId: number
  onSave: (success?: boolean) => void
  onSavingChange?: (loading: boolean) => void
  handleSnackbar: (msg: string | undefined, status: boolean) => void
}

const isSameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase()

export const CourierFormEditNew = forwardRef<
  CourierFormEditNewRef,
  CourierFormEditNewProps
>(({ courierId, onSave, onSavingChange, handleSnackbar }, ref) => {
  const { state, callApi } = useApi<Courier>()
  const { callApi: callSaveApi } = useApi()

  const {
    getTypesPriority,
    typesPriorityState: { typesPriority }
  } = useTypesPriority()

  const [original, setOriginal] = useState<Courier | null>(null)
  const [name, setName] = useState('')
  const [hasPriorization, setHasPriorization] = useState(false)
  const [active, setActive] = useState(true)
  const [draftServices, setDraftServices] = useState<DraftService[]>([])

  const [newServiceName, setNewServiceName] = useState('')
  const [newServiceTypePriorityId, setNewServiceTypePriorityId] = useState(0)
  const [newServiceActive, setNewServiceActive] = useState(true)
  const [newServiceError, setNewServiceError] = useState('')

  const [editingServiceId, setEditingServiceId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingTypePriorityId, setEditingTypePriorityId] = useState(0)
  const [editingActive, setEditingActive] = useState(true)
  const [editingError, setEditingError] = useState('')

  const deleteServiceModal = useModal<{ service: DraftService }>(
    'Eliminar servicio'
  )
  const deactivateCourierModal = useModal('Inactivar courier')

  const tempIdRef = useRef(0)

  useEffect(() => {
    if (typesPriority?.length === 0) {
      getTypesPriority()
    }
    callApi(getCourier(courierId)).then((response: Courier) => {
      setOriginal(response)
      setName(response.name)
      setHasPriorization(response.hasPriorization)
      setActive(response.active)
      setDraftServices(
        response.courierServices.map((service) => ({
          ...service,
          isNew: false,
          isDeleted: false
        }))
      )
    })
  }, [])

  const activeServices = draftServices.filter((s) => !s.isDeleted)

  const isDuplicateName = (candidate: string, ignoreCourierServiceId?: number) =>
    activeServices.some(
      (s) =>
        s.courierServiceId !== ignoreCourierServiceId &&
        isSameName(s.name, candidate)
    )

  const isCreateServiceDisabled =
    newServiceName.trim().length === 0 ||
    (hasPriorization && newServiceTypePriorityId <= 0)

  const handleCreateService = () => {
    setNewServiceError('')
    if (isDuplicateName(newServiceName)) {
      setNewServiceError('Ya existe un servicio con ese nombre')
      return
    }
    tempIdRef.current -= 1
    setDraftServices((prev) => [
      ...prev,
      {
        courierServiceId: tempIdRef.current,
        name: newServiceName.trim(),
        price: 0,
        createdAt: new Date(),
        active: newServiceActive,
        typePriorityId: newServiceTypePriorityId > 0 ? newServiceTypePriorityId : null,
        isNew: true,
        isDeleted: false
      }
    ])
    setNewServiceName('')
    setNewServiceTypePriorityId(0)
    setNewServiceActive(true)
  }

  const startEditService = (service: DraftService) => {
    setEditingServiceId(service.courierServiceId)
    setEditingName(service.name)
    setEditingTypePriorityId(service.typePriorityId || 0)
    setEditingActive(service.active)
    setEditingError('')
  }

  const cancelEditService = () => {
    setEditingServiceId(null)
    setEditingError('')
  }

  const confirmEditService = () => {
    if (editingServiceId === null) return
    if (isDuplicateName(editingName, editingServiceId)) {
      setEditingError('Ya existe un servicio con ese nombre')
      return
    }
    setDraftServices((prev) =>
      prev.map((s) =>
        s.courierServiceId === editingServiceId
          ? {
              ...s,
              name: editingName.trim(),
              typePriorityId:
                editingTypePriorityId > 0 ? editingTypePriorityId : null,
              active: editingActive
            }
          : s
      )
    )
    setEditingServiceId(null)
  }

  const requestDeleteService = (service: DraftService) => {
    if (service.isNew) {
      setDraftServices((prev) =>
        prev.filter((s) => s.courierServiceId !== service.courierServiceId)
      )
    } else {
      setDraftServices((prev) =>
        prev.map((s) =>
          s.courierServiceId === service.courierServiceId
            ? { ...s, isDeleted: true }
            : s
        )
      )
    }
    deleteServiceModal.closeModal()
  }

  const priorityLabel = (typePriorityId: number | null) =>
    typesPriority?.find((t) => t.typePriorityId === typePriorityId)?.name ||
    'Sin prioridad'

  const handleSaveAll = () => {
    if (!original) return

    const requests: Promise<any>[] = []

    if (
      name.trim() !== original.name ||
      hasPriorization !== original.hasPriorization ||
      active !== original.active
    ) {
      requests.push(
        putCourier(courierId, { name: name.trim(), hasPriorization, active })
      )
    }

    draftServices.forEach((service) => {
      if (service.isNew && !service.isDeleted) {
        requests.push(
          postCourierService(courierId, {
            name: service.name,
            typePriorityId: service.typePriorityId,
            active: service.active
          })
        )
        return
      }

      if (!service.isNew && service.isDeleted) {
        requests.push(deleteCourierService(courierId, service.courierServiceId))
        return
      }

      if (!service.isNew && !service.isDeleted) {
        const originalService = original.courierServices.find(
          (s) => s.courierServiceId === service.courierServiceId
        )
        if (
          originalService &&
          (originalService.name !== service.name ||
            originalService.typePriorityId !== service.typePriorityId ||
            originalService.active !== service.active)
        ) {
          requests.push(
            putCourierService(courierId, service.courierServiceId, {
              name: service.name,
              typePriorityId: service.typePriorityId,
              active: service.active
            })
          )
        }
      }
    })

    if (requests.length === 0) {
      onSave(true)
      return
    }

    onSavingChange?.(true)
    callSaveApi(Promise.all(requests) as unknown as Promise<AxiosResponse<any>>)
      .then(() => {
        handleSnackbar('Courier actualizado correctamente', true)
        onSave(true)
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
      .finally(() => onSavingChange?.(false))
  }

  useImperativeHandle(ref, () => ({ save: handleSaveAll }))

  const columns = createServiceColumns(priorityLabel)

  const actions = [
    createAction<DraftService>({
      key: 'edit',
      label: 'Editar',
      icon: <Edit fontSize='small' />,
      onClick: startEditService
    }),
    createAction<DraftService>({
      key: 'delete',
      label: 'Eliminar',
      icon: <DeleteIcon fontSize='small' />,
      onClick: (service) => deleteServiceModal.openModal('xs', { service })
    })
  ]

  if (state.status === Status.Loading || !original) {
    return <Loading />
  }

  return (
    <Fragment>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Paper variant='outlined' sx={{ ...sectionPaperSx, pt: 1 }}>
            <Typography variant='overline' style={sectionTitleSx}>
              Información del Courier
            </Typography>
            <Grid container spacing={0.5}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Nombre del courier'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                />
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', gap: 3 }}>
                <FormControlLabel
                  sx={{ my: -0.5 }}
                  control={
                    <Checkbox
                      checked={active}
                      color='primary'
                      onChange={(_e, checked) => {
                        if (!checked) {
                          deactivateCourierModal.openModal('xs')
                        } else {
                          setActive(true)
                          setDraftServices((prev) =>
                            prev.map((s) => (!s.active ? { ...s, active: true } : s))
                          )
                        }
                      }}
                    />
                  }
                  label='Activo'
                />
                <FormControlLabel
                  sx={{ my: -0.5 }}
                  control={
                    <Checkbox
                      checked={hasPriorization}
                      color='primary'
                      onChange={(_e, checked) => setHasPriorization(checked)}
                    />
                  }
                  label='Priorizar courier'
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: 'action.hover'
                  }}
                >
                  <InfoOutlined sx={{ fontSize: '1rem' }} color='primary' />
                  <Typography variant='caption' color='black'>
                    La priorización solo aplica a almacenamiento externo.
                    Define el orden de asignación de rutas automáticas.
                  </Typography>
                </Box>
              </Grid>
              {hasPriorization !== original.hasPriorization && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: '#FFF4E5',
                      border: '1px solid #FFB300'
                    }}
                  >
                    <WarningAmberOutlined sx={{ fontSize: '1.25rem', color: '#FFB300' }} />
                    <Typography variant='caption' color='black'>
                      Este cambio de priorización solo aplicará a los pedidos nuevos
                      de marcas con operación externa. Los pedidos ya ingresados
                      no se verán afectados.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper variant='outlined' sx={{ ...sectionPaperSx, pt: 1 }}>
            <Typography variant='overline' style={sectionTitleSx}>
              Agregar Nuevo Servicio
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Nombre del servicio'
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  inputProps={{ maxLength: NAME_MAX_LENGTH }}
                  error={Boolean(newServiceError)}
                  helperText={newServiceError}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <PriorizationDDL
                  value={newServiceTypePriorityId}
                  onChange={(e) =>
                    setNewServiceTypePriorityId(Number(e.target.value))
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  sx={{ height: 56, whiteSpace: 'nowrap' }}
                  disabled={isCreateServiceDisabled}
                  onClick={handleCreateService}
                >
                  Crear servicio
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper variant='outlined' sx={{ ...sectionPaperSx, pt: 1, pb: 3 }}>
            <Typography variant='overline' style={sectionTitleSx}>
              Servicios Configurados
            </Typography>
            <Box sx={{ mb: '5px' }}>
              <GenericTableV2<DraftService>
              data={activeServices}
              columns={
                editingServiceId === null
                  ? columns
                  : createColumns<DraftService>([
                      {
                        key: 'name',
                        title: 'Servicio',
                        align: 'left',
                        render: (_value, row) =>
                          row.courierServiceId === editingServiceId ? (
                            <TextField
                              fullWidth
                              variant='outlined'
                              size='small'
                              value={editingName}
                              error={Boolean(editingError)}
                              helperText={editingError}
                              inputProps={{ maxLength: NAME_MAX_LENGTH }}
                              onChange={(e) => setEditingName(e.target.value)}
                            />
                          ) : (
                            row.name
                          )
                      },
                      {
                        key: 'typePriorityId',
                        title: 'Tipo de Prioridad',
                        align: 'left',
                        render: (value, row) =>
                          row.courierServiceId === editingServiceId ? (
                            <PriorizationDDL
                              hideLabel
                              size='small'
                              value={editingTypePriorityId}
                              onChange={(e) =>
                                setEditingTypePriorityId(Number(e.target.value))
                              }
                            />
                          ) : (
                            priorityLabel(value as number | null)
                          )
                      },
                      {
                        key: 'active',
                        title: 'Activo',
                        align: 'center',
                        render: (value, row) =>
                          row.courierServiceId === editingServiceId ? (
                            <Checkbox
                              checked={editingActive}
                              color='primary'
                              onChange={(_e, checked) => setEditingActive(checked)}
                            />
                          ) : (
                            <YesNoBadge value={value as boolean} />
                          )
                      }
                    ])
              }
              keyField='courierServiceId'
              emptyMessage='Este courier todavía no tiene servicios configurados'
              actions={
                editingServiceId === null
                  ? actions
                  : [
                      createAction<DraftService>({
                        key: 'confirm',
                        label: 'Confirmar',
                        icon: <Check fontSize='small' />,
                        show: (row) => row.courierServiceId === editingServiceId,
                        onClick: confirmEditService
                      }),
                      createAction<DraftService>({
                        key: 'cancel',
                        label: 'Cancelar',
                        icon: <Close fontSize='small' />,
                        show: (row) => row.courierServiceId === editingServiceId,
                        onClick: cancelEditService
                      })
                    ]
              }
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Modal
        open={deleteServiceModal.isOpen}
        maxWidth={deleteServiceModal.size}
        onClose={deleteServiceModal.closeModal}
        title={deleteServiceModal.title}
        cancelButton={{ title: 'Cancelar', action: deleteServiceModal.closeModal }}
        successButton={{
          title: 'Eliminar',
          action: () => {
            const service = deleteServiceModal.extendedProps?.service
            if (service) requestDeleteService(service)
          }
        }}
      >
        <Typography variant='body2' component='h4'>
          ¿Está seguro que desea eliminar el servicio{' '}
          {deleteServiceModal.extendedProps?.service.name}?
        </Typography>
      </Modal>

      <Modal
        open={deactivateCourierModal.isOpen}
        maxWidth={deactivateCourierModal.size}
        onClose={deactivateCourierModal.closeModal}
        title={deactivateCourierModal.title}
        cancelButton={{ title: 'Cancelar', action: deactivateCourierModal.closeModal }}
        successButton={{
          title: 'Inactivar',
          action: () => {
            setActive(false)
            setDraftServices((prev) =>
              prev.map((s) => (s.active ? { ...s, active: false } : s))
            )
            deactivateCourierModal.closeModal()
          }
        }}
      >
        <Typography variant='body2' component='h4'>
          Al inactivar este courier también se inactivarán todos los servicios
          courier asociados a él. ¿Desea continuar?
        </Typography>
      </Modal>
    </Fragment>
  )
})

CourierFormEditNew.displayName = 'CourierFormEditNew'
